// @flow

import * as React from 'react';

import type { FormApi, FormValuesShape } from 'final-form';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';
import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { needsAuth } from '@gandi/authentication';
import { getCart } from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';
import { Form } from '@gandi/form/FinalForm';
import { notifyDanger } from '@gandi/notifications';
import { selectCurrentId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Button, Base } from '@gandi/ui';

import { createLogger } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useMailboxOffers } from 'src/hooks/useMailboxOffers';
import { selectAdminURL } from 'src/modules/config';
import { fetchMailboxOffers } from 'src/modules/mailbox/offers';

import { Intro } from '../Layout/components/Intro';
import { Layout } from '../components/Layout';

import type { WithRouterProps } from '../helpers/withRouter';

import type {
  FullState,
  PreFetchArgs,
  PreFetchReturn,
  WithNeedsAuthProps,
} from '../types';

import { MailboxDurationChoiceGroup } from './components/MailboxDurationChoiceGroup';
import { MailboxSummary } from './components/MailboxSummary';
import { MailboxTypeChoiceGroup } from './components/MailboxTypeChoiceGroup';
import {
  MAILBOX_COUNT_INPUT_NAME,
  MAILBOX_DURATION_INPUT_NAME,
  MAILBOX_TYPE_INPUT_NAME,
  initialFormValuesDefault,
} from './consts';
import { useMailboxCatalog } from './hooks';
import { addMailboxProducts, fetchMailboxesPrices } from './modules/actions';

import type { FormValues } from './types/form';

type Params = {
  fqdn: Fqdn,
  sharingId?: string,
};

export type Props = {
  fqdn: Fqdn,
};

const logger = createLogger('Mailbox create');

const resetFormValues = (form: FormApi<FormValuesShape>) => {
  form.batch(() => {
    form.change(
      MAILBOX_TYPE_INPUT_NAME,
      initialFormValuesDefault[MAILBOX_TYPE_INPUT_NAME],
    );
    form.change(
      MAILBOX_DURATION_INPUT_NAME,
      initialFormValuesDefault[MAILBOX_DURATION_INPUT_NAME],
    );
    form.change(
      MAILBOX_COUNT_INPUT_NAME,
      initialFormValuesDefault[MAILBOX_COUNT_INPUT_NAME],
    );
  });
};

export function MailboxCreateForm({ fqdn }: Props): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  useMailboxOffers();
  const { prices } = useMailboxCatalog();

  const adminUrl = useAppSelector(selectAdminURL);

  React.useEffect(() => {
    /**
     * Fetch prices if needed.
     */
    if (isEmpty(prices)) {
      dispatch(fetchMailboxesPrices()).catch(err => {
        dispatch(logger.warn({ err }, 'Error when fetching mailboxes prices'));
        dispatch(
          notifyDanger({
            message: __(
              'An error has occurred while fetching prices. Please try again.',
            ),
          }),
        );
      });
    }

    // DidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Call the handleSubmit method to add mailboxes to the cart,
   * and reinit counter values.
   */
  const handleSubmit = (
    formData: FormValues,
    form: FormApi<FormValuesShape>,
  ): void => {
    if (!formData.type || !formData.count) {
      return;
    }

    const duration = parseInt(formData.duration, 10);

    dispatch(
      addMailboxProducts(
        {
          fqdn,
          // $FlowFixMe[incompatible-call] formData.type cannot be empty string at this point. It is checked above.
          mailbox_type: formData.type,
          duration,
          autorenew: duration === 1,
        },
        formData.count,
      ),
    )
      .then(() => {
        resetFormValues(form);
        return dispatch(getCart());
      })
      .catch(err => {
        dispatch(
          logger.warn(
            { err },
            'Unable to add or remove mailboxes into the cart.',
          ),
        );

        const error = err.products.find(product => product.body.message);

        dispatch(
          notifyDanger({
            message:
              error?.body.message ||
              __('An error has occurred. Please try again.'),
          }),
        );
      });
  };

  return (
    // $FlowFixMe[incompatible-indexer]
    <Form onSubmit={handleSubmit} initialValues={initialFormValuesDefault}>
      <Base gap="base" flex flexCol>
        <MailboxTypeChoiceGroup />
        <MailboxDurationChoiceGroup />
        <MailboxSummary>
          <Button
            href={`${adminUrl}/domain/${fqdn}/mail/mailboxes`}
            kind="outline"
          >
            {__('Cancel')}
          </Button>
        </MailboxSummary>
      </Base>
    </Form>
  );
}

const MailboxCreateLayout = ({
  params: { fqdn },
}: {
  ...WithRouterProps<Params>,
  ...WithNeedsAuthProps,
}) => {
  const { __ } = useTranslator();

  return (
    <Layout>
      <Intro
        title={__('Buy more mailboxes for the domain %(fqdn)s', { fqdn })}
      />
      <MailboxCreateForm fqdn={fqdn} />
    </Layout>
  );
};

MailboxCreateLayout.handleRedirect = (
  args: StaticHandlerArgs<>,
): Promise<mixed> => {
  return Layout.handleRedirect(args);
};

MailboxCreateLayout.preFetch = (
  args: PreFetchArgs<{}, void, empty, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState } = args;
  return Layout.preFetch(args).then(() => {
    const sharingId = selectCurrentId(getState());
    return Promise.allSettled([
      dispatch(fetchMailboxesPrices()),
      dispatch(fetchMailboxOffers(sharingId)),
    ]);
  });
};

/**
 * Create cart if needed,
 * And add mailboxes products into the cstage.art.
 */
const MailboxCreate: React.ComponentType<{}> = compose(
  needsAuth,
  withRouter,
  withPageMeta(args => ({
    title: args.__('Buy mailboxes for your domain - Gandi.net'),
    description: args.__('Buy mailboxes for your domain.'),
  })),
)(MailboxCreateLayout);

export default MailboxCreate;
